import { useEffect, useRef, useState, useCallback } from "react";
import mermaid from "mermaid";
import { AlertCircle, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

interface MermaidRendererProps {
  code: string;
  className?: string;
}

// Initialize mermaid once
let mermaidInitialized = false;
const initializeMermaid = () => {
  if (!mermaidInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      theme: "default",
      securityLevel: "loose",
      fontFamily: "inherit",
    });
    mermaidInitialized = true;
  }
};

export function MermaidRenderer({
  code,
  className = "",
}: MermaidRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);

  const renderDiagram = useCallback(async () => {
    if (!containerRef.current || !code?.trim()) {
      setIsLoading(false);
      return;
    }

    initializeMermaid();
    setIsLoading(true);
    setError(null);

    try {
      // Decode HTML entities
      const decodedCode = code
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();

      const id = `mermaid-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const { svg } = await mermaid.render(id, decodedCode);

      if (containerRef.current) {
        containerRef.current.innerHTML = svg;
        // Make SVG responsive
        const svgEl = containerRef.current.querySelector("svg");
        if (svgEl) {
          svgEl.style.maxWidth = "100%";
          svgEl.style.height = "auto";
          svgEl.style.transform = `scale(${scale})`;
          svgEl.style.transformOrigin = "center center";
        }
      }
    } catch (err) {
      console.error("Mermaid render error:", err);
      setError(
        err instanceof Error ? err.message : "Error al renderizar diagrama",
      );
    } finally {
      setIsLoading(false);
    }
  }, [code, scale]);

  useEffect(() => {
    renderDiagram();
  }, [renderDiagram]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 2));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleReset = () => {
    setScale(1);
  };

  if (!code?.trim()) {
    return null;
  }

  if (error) {
    return (
      <div
        className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}
      >
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-600 text-sm font-medium">
              Error en diagrama
            </p>
            <p className="text-xs text-red-500 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mermaid-container my-6 ${className}`}>
      {/* Zoom controls */}
      <div className="flex items-center justify-end gap-1 mb-2">
        <button
          type="button"
          onClick={handleZoomOut}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title="Reducir"
          disabled={scale <= 0.5}
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-xs text-gray-500 min-w-[3rem] text-center">
          {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          onClick={handleZoomIn}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title="Ampliar"
          disabled={scale >= 2}
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleReset}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title="Restablecer"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {/* Diagram container */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 overflow-x-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-gray-400">
              Cargando diagrama...
            </div>
          </div>
        )}
        <div
          ref={containerRef}
          className={`flex justify-center items-center min-h-[100px] transition-transform duration-200 ${
            isLoading ? "hidden" : ""
          }`}
        />
      </div>
    </div>
  );
}
